import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import moment from "moment";
import { leavePalette, cardSx } from "../Leave/leaveHelpers";

// Attendance Correction reuses the exact visual tokens the redesigned Leave /
// Work Adjustment screens already established, so all of My Zone > My Requests
// reads as one system and a palette tweak stays a single edit.
export { leavePalette, cardSx };

// Per-correction-type presentation. `label` is the chip/column text, `color`
// its accent (soft-tinted pill background derived as `${color}1A`). `matches`
// keeps type filtering spelling-tolerant against whatever the API returns.
export const CORRECTION_TYPES = {
  missing_in: {
    key: "missing_in",
    label: "Missing Punch (In)",
    color: leavePalette.blue,
    icon: FingerprintOutlinedIcon,
  },
  missing_out: {
    key: "missing_out",
    label: "Missing Punch (Out)",
    color: leavePalette.purple,
    icon: FingerprintOutlinedIcon,
  },
  missing_both: {
    key: "missing_both",
    label: "Missing Punch (In & Out)",
    color: leavePalette.amber,
    icon: FingerprintOutlinedIcon,
  },
  incorrect_in: {
    key: "incorrect_in",
    label: "Incorrect Punch (In)",
    color: leavePalette.green,
    icon: EditCalendarOutlinedIcon,
  },
  incorrect_out: {
    key: "incorrect_out",
    label: "Incorrect Punch (Out)",
    color: leavePalette.purple,
    icon: EditCalendarOutlinedIcon,
  },
  incorrect_both: {
    key: "incorrect_both",
    label: "Incorrect Punch (In & Out)",
    color: leavePalette.red,
    icon: EditCalendarOutlinedIcon,
  },
};

// Ordered list for the "Correction Type" select in the create drawer.
export const CORRECTION_TYPE_OPTIONS = Object.values(CORRECTION_TYPES).map(
  (t) => ({ id: t.key, label: t.label }),
);

export const correctionMeta = (typeKey) =>
  CORRECTION_TYPES[typeKey] || {
    key: typeKey,
    label: typeKey || "—",
    color: leavePalette.gray,
    icon: EditCalendarOutlinedIcon,
  };

// Canonical status bucket used by the KPI tiles and the status filter.
export const canonicalStatus = (status) => {
  const k = (status || "").trim().toLowerCase();
  if (k === "approved") return "Approved";
  if (k === "pending") return "Pending";
  if (k === "rejected") return "Rejected";
  if (k === "cancelled" || k === "canceled") return "Cancelled";
  return status;
};

// Status pill colour. Falls back to the row's own color_code when the status
// isn't one of the known four, so a custom workflow status still renders.
export const statusMeta = (status, colorCode) => {
  const key = (status || "").trim().toLowerCase();
  const map = {
    approved: leavePalette.green,
    pending: leavePalette.amber,
    rejected: leavePalette.red,
    cancelled: leavePalette.gray,
    canceled: leavePalette.gray,
  };
  return map[key] || colorCode || leavePalette.blue;
};

// Format a punch pair the way the table shows it. When BOTH sides are missing
// the cell collapses to a single "--" (matches the reference design); otherwise
// each side renders on its own In:/Out: line.
export const punchPair = (inVal, outVal) => ({
  in: inVal || "",
  out: outVal || "",
  empty: !inVal && !outVal,
});

// A punch time picked in the create drawer (a dayjs value) -> "HH:mm".
export const formatPunch = (value) =>
  value && value.isValid && value.isValid() ? value.format("HH:mm") : "";

// The "New Attendance Correction" drawer needs the original punch as read-only
// context; for the demo dataset it's derived from the correction type.
export const originalForType = (typeKey, row = {}) => {
  const meta = correctionMeta(typeKey);
  return {
    original_in: row.original_in || "",
    original_out: row.original_out || "",
    label: meta.label,
  };
};

// Column definitions shared by the CustomFilter toolbar (search / column
// filters / sort / saved views) and the history table. `type` drives how the
// shared filter treats each column; the table renders punches/status/type
// specially by `key` and falls back to the flat row value otherwise.
export const CORRECTION_HEADERS = [
  { name: "Date", key: "date_display", type: "date" },
  { name: "Reason", key: "reason", type: "string" },
  { name: "Status", key: "status", type: "autocomplete" },
  { name: "Applied On", key: "applied_display", type: "date" },
];

const punchText = (inVal, outVal) =>
  !inVal && !outVal
    ? "--"
    : `In: ${inVal || "--"} Out: ${outVal || "--"}`;

// Normalize a record for the shared filter/search/sort: every visible column
// must address a flat row[key]. Composite punches get a searchable string form
// alongside the raw fields the table reads for its two-line render.
export const toFilterRow = (r) => ({
  ...r,
  correction_label: correctionMeta(r.correction_type).label,
  // r.date is "YYYY-MM-DD"; the grid shows DD-MM-YYYY like every other date.
  date_display: r.date ? moment(r.date, "YYYY-MM-DD").format("DD-MM-YYYY") : "",
  original_display: punchText(r.original_in, r.original_out),
  corrected_display: punchText(r.corrected_in, r.corrected_out),
  applied_display: r.applied_on
    ? moment(r.applied_on).format("DD-MM-YYYY, HH:mm")
    : "",
});

// Backend endpoint (My Workspace only). `domain` already ends with `api/`, so
// this is appended directly: `${domain}${ATTENDANCE_CORRECTION_ENDPOINT}`.
export const ATTENDANCE_CORRECTION_ENDPOINT =
  "my_zone/apply/attendance_correction/myzone-attendance-correction/";

const PUNCH_TIME_FORMATS = ["HH:mm", "HH:mm:ss", "hh:mm A", "h:mm A"];

// A punch's full moment when it has a (YYYY-MM-DD) date, so an overnight
// shift's after-midnight punches sort after the evening ones; time-only
// otherwise.
const parsePunchTime = (t, date) => {
  const withDate = date
    ? moment(
        `${date} ${t}`,
        PUNCH_TIME_FORMATS.map((format) => `YYYY-MM-DD ${format}`),
        true,
      )
    : null;
  return withDate && withDate.isValid()
    ? withDate
    : moment(String(t), PUNCH_TIME_FORMATS, true);
};

export const normalizePunchEntries = (entries = [], fallbackDate = null) =>
  (Array.isArray(entries) ? entries : [])
    .filter(Boolean)
    .map((entry, index) => {
      const punchDate = entry.date || entry.punch_date || fallbackDate || "";
      const punchTime =
        entry.time ||
        entry.punch_time ||
        entry.punch ||
        (entry.punch_datetime
          ? moment(entry.punch_datetime).format("HH:mm")
          : "");
      return {
        ...entry,
        punch_id: entry.punch_id || entry.id || index + 1,
        date: punchDate,
        time: punchTime,
        direction: String(entry.direction || entry.punch_direction || "")
          .toUpperCase(),
      };
    })
    .filter((entry) => entry.time && ["IN", "OUT"].includes(entry.direction));

// Collapse an API punch-entries list (either `entries` or `original_entries`
// — both share the same {direction, time} shape) into a first-IN / last-OUT
// display pair (e.g. "09:00"). Exported so Team Zone's approve-side
// helpers (attendanceCorrectionApproveHelpers) can derive both the requested
// AND original pair from the same logic instead of re-deriving it.
export const derivePairFromEntries = (entries = []) => {
  const fmt = (m) => (m && m.isValid() ? m.format("HH:mm") : "");
  let earliestIn = null;
  let latestOut = null;
  normalizePunchEntries(entries).forEach((e) => {
    const dir = (e.direction || "").toUpperCase();
    const m = parsePunchTime(e.time, e.date);
    if (!m.isValid()) return;
    if (dir === "IN" && (!earliestIn || m.isBefore(earliestIn))) earliestIn = m;
    if (dir === "OUT" && (!latestOut || m.isAfter(latestOut))) latestOut = m;
  });
  return { in: fmt(earliestIn), out: fmt(latestOut) };
};

const punchKeys = (entries, direction) =>
  normalizePunchEntries(entries)
    .filter((e) => e.direction === direction)
    .map((e) => `${e.date || ""} ${String(e.time).slice(0, 5)}`)
    .sort()
    .join("|");

// Fallback only -- the API sends `correction_type` itself (the same rule, in
// My_Zone/Apply/Attendance_Correction/views.py infer_correction_type), so My
// Workspace and Team Workspace always agree. A direction with no punch on
// record that the request adds is "missing"; one whose punches differ is
// "incorrect"; an unchanged direction doesn't count.
export const inferCorrectionType = (entries = [], originalEntries = []) => {
  const missing = [];
  const incorrect = [];
  ["IN", "OUT"].forEach((direction) => {
    const requested = punchKeys(entries, direction);
    const original = punchKeys(originalEntries, direction);
    if (requested && !original) missing.push(direction);
    else if (requested !== original) incorrect.push(direction);
  });
  if (missing.length === 2) return "missing_both";
  if (missing.length) return missing[0] === "IN" ? "missing_in" : "missing_out";
  if (incorrect.length === 2) return "incorrect_both";
  if (incorrect.length) return incorrect[0] === "IN" ? "incorrect_in" : "incorrect_out";
  return null;
};

// The API's raw_date is "YYYY-MM-DD" (older builds sent "DD-MM-YY") and date is
// "DD-MM-YYYY"; normalize to "YYYY-MM-DD" -- the format the PUT/POST endpoints
// expect back.
export const toIsoRequestDate = (value) => {
  if (!value) return null;
  const parsed = moment(String(value), ["YYYY-MM-DD", "DD-MM-YYYY", "DD-MM-YY"], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

// Stable, unique row key. The API returns one item per user + date + status
// (a day can hold e.g. a Cancelled request and a newer Pending one, which
// share an attendance_record_id), so all three are needed to be unique.
export const correctionRowId = (row, rawDate, index) =>
  [row.user_id, rawDate || `row${index}`, row.status].filter(Boolean).join("-");

// Map one grouped correction request from the GET API into the flat record shape
// the tiles / table / CustomFilter already consume.
export const mapCorrectionApiRow = (row, index = 0) => {
  const rawDate = toIsoRequestDate(row.raw_date) || toIsoRequestDate(row.date);
  const entries = normalizePunchEntries(row.entries, rawDate);
  const originalEntries = normalizePunchEntries(row.original_entries, rawDate);
  const pair = derivePairFromEntries(entries);
  const originalPair = derivePairFromEntries(originalEntries);
  const appliedIso = row.applied_on
    ? moment(row.applied_on, "DD-MM-YYYY, HH:mm").toISOString()
    : "";
  return {
    id: correctionRowId(row, rawDate, index),
    request_id: rawDate ? `ACR-${rawDate}` : `ACR-${index}`,
    user_id: row.user_id,
    date: rawDate,
    correction_type:
      row.correction_type !== undefined
        ? row.correction_type
        : inferCorrectionType(entries, originalEntries),
    original_in: originalPair.in,
    original_out: originalPair.out,
    corrected_in: pair.in,
    corrected_out: pair.out,
    entries,
    original_entries: originalEntries,
    early_exit: Boolean(row.early_exit),
    reason: row.reason || "",
    status: canonicalStatus(row.status) || "Pending",
    color_code: row.color_code,
    applied_on: appliedIso,
    applied_by: row.applied_by,
    approved_by: row.approved_by,
    approved_at: row.approved_at,
    rejected_by: row.rejected_by,
    rejected_at: row.rejected_at,
    rejected_reason: row.rejected_reason,
    cancelled_by: row.cancelled_by,
    cancelled_at: row.cancelled_at,
    cancelled_reason: row.cancelled_reason,
    approver_name: row.approver_name,
    edit_audit_trail: row.edit_audit_trail || [],
    attendance_record_id: row.attendance_record_id ?? null,
    shift_id: row.shift_id ?? null,
  };
};

// --- Demo dataset ---------------------------------------------------------
// No backend endpoint exists yet for attendance corrections, so the screen is
// seeded from this in-memory list (it reproduces the reference design's 18
// records). Swap `useCorrectionData` for a `useFetch(...)` call once the API is
// available — every consumer already reads a flat records array, so nothing
// else on the screen has to change.
export const SEED_CORRECTIONS = [
  { id: 18, request_id: "ACR-2026-0018", date: "2026-08-21", correction_type: "missing_in", original_in: "", original_out: "", corrected_in: "09:00 AM", corrected_out: "05:30 PM", reason: "Forgot to punch in", status: "Pending", applied_on: "2026-08-20T10:15:00" },
  { id: 17, request_id: "ACR-2026-0017", date: "2026-08-19", correction_type: "missing_out", original_in: "09:05 AM", original_out: "", corrected_in: "09:05 AM", corrected_out: "06:00 PM", reason: "Forgot to punch out", status: "Approved", applied_on: "2026-08-19T14:30:00" },
  { id: 16, request_id: "ACR-2026-0016", date: "2026-08-18", correction_type: "incorrect_in", original_in: "09:45 AM", original_out: "05:30 PM", corrected_in: "09:00 AM", corrected_out: "05:30 PM", reason: "Wrong in time", status: "Approved", applied_on: "2026-08-18T11:05:00" },
  { id: 15, request_id: "ACR-2026-0015", date: "2026-08-16", correction_type: "missing_both", original_in: "", original_out: "", corrected_in: "09:00 AM", corrected_out: "06:00 PM", reason: "Worked on weekend, forgot to punch", status: "Pending", applied_on: "2026-08-16T19:45:00" },
  { id: 14, request_id: "ACR-2026-0014", date: "2026-08-14", correction_type: "incorrect_out", original_in: "09:10 AM", original_out: "04:30 PM", corrected_in: "09:10 AM", corrected_out: "05:45 PM", reason: "Wrong out time", status: "Rejected", applied_on: "2026-08-15T09:20:00" },
  { id: 13, request_id: "ACR-2026-0013", date: "2026-08-12", correction_type: "missing_out", original_in: "09:07 AM", original_out: "", corrected_in: "09:07 AM", corrected_out: "06:05 PM", reason: "Forgot to punch out", status: "Approved", applied_on: "2026-08-12T18:15:00" },
  { id: 12, request_id: "ACR-2026-0012", date: "2026-08-10", correction_type: "incorrect_in", original_in: "10:20 AM", original_out: "06:00 PM", corrected_in: "09:00 AM", corrected_out: "06:00 PM", reason: "Wrong in time", status: "Cancelled", applied_on: "2026-08-10T15:40:00" },
  { id: 11, request_id: "ACR-2026-0011", date: "2026-08-08", correction_type: "missing_in", original_in: "", original_out: "05:50 PM", corrected_in: "09:00 AM", corrected_out: "05:50 PM", reason: "System not available", status: "Pending", applied_on: "2026-08-08T11:30:00" },
  { id: 10, request_id: "ACR-2026-0010", date: "2026-08-07", correction_type: "incorrect_out", original_in: "09:15 AM", original_out: "04:15 PM", corrected_in: "09:15 AM", corrected_out: "05:30 PM", reason: "Wrong out time", status: "Approved", applied_on: "2026-08-07T17:40:00" },
  { id: 9, request_id: "ACR-2026-0009", date: "2026-08-05", correction_type: "missing_both", original_in: "", original_out: "", corrected_in: "09:00 AM", corrected_out: "06:00 PM", reason: "Worked from client site", status: "Rejected", applied_on: "2026-08-05T20:20:00" },
  { id: 8, request_id: "ACR-2026-0008", date: "2026-08-04", correction_type: "missing_in", original_in: "", original_out: "05:30 PM", corrected_in: "09:00 AM", corrected_out: "05:30 PM", reason: "Forgot to punch in", status: "Pending", applied_on: "2026-08-04T10:05:00" },
  { id: 7, request_id: "ACR-2026-0007", date: "2026-08-02", correction_type: "missing_both", original_in: "", original_out: "", corrected_in: "09:00 AM", corrected_out: "06:00 PM", reason: "Weekend work", status: "Approved", applied_on: "2026-08-02T19:10:00" },
  { id: 6, request_id: "ACR-2026-0006", date: "2026-08-01", correction_type: "incorrect_in", original_in: "09:35 AM", original_out: "05:30 PM", corrected_in: "09:00 AM", corrected_out: "05:30 PM", reason: "Wrong in time", status: "Pending", applied_on: "2026-08-01T09:50:00" },
  { id: 5, request_id: "ACR-2026-0005", date: "2026-07-30", correction_type: "missing_out", original_in: "09:02 AM", original_out: "", corrected_in: "09:02 AM", corrected_out: "06:00 PM", reason: "Forgot to punch out", status: "Approved", applied_on: "2026-07-30T18:20:00" },
  { id: 4, request_id: "ACR-2026-0004", date: "2026-07-29", correction_type: "incorrect_out", original_in: "09:00 AM", original_out: "04:00 PM", corrected_in: "09:00 AM", corrected_out: "05:30 PM", reason: "Wrong out time", status: "Pending", applied_on: "2026-07-29T08:30:00" },
  { id: 3, request_id: "ACR-2026-0003", date: "2026-07-28", correction_type: "missing_in", original_in: "", original_out: "05:45 PM", corrected_in: "09:10 AM", corrected_out: "05:45 PM", reason: "Biometric error", status: "Approved", applied_on: "2026-07-28T11:15:00" },
  { id: 2, request_id: "ACR-2026-0002", date: "2026-07-26", correction_type: "missing_both", original_in: "", original_out: "", corrected_in: "09:00 AM", corrected_out: "06:00 PM", reason: "Client site visit", status: "Pending", applied_on: "2026-07-26T17:05:00" },
  { id: 1, request_id: "ACR-2026-0001", date: "2026-07-25", correction_type: "incorrect_in", original_in: "10:00 AM", original_out: "06:00 PM", corrected_in: "09:00 AM", corrected_out: "06:00 PM", reason: "Wrong in time", status: "Approved", applied_on: "2026-07-25T16:25:00" },
];
